import React, { useState, useEffect, useReducer, useRef } from "react";
import { toast } from "react-toastify";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import MobileActionMenu from "../../components/MobileActionMenu";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import PublishIcon from "@material-ui/icons/Publish";
import UnpublishedIcon from "@material-ui/icons/VisibilityOff";
import AddIcon from "@material-ui/icons/Add";
import ArticleIcon from "@material-ui/icons/Description";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { listBlogPosts, createBlogPost, updateBlogPost, deleteBlogPost, uploadBlogImagem } from "../../services/blogPostService";
import { getBackendUrl } from "../../config";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../../components/ConfirmationModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    padding: "24px",
    backgroundColor: "#f8f9fa",
    gap: 16,
    [theme.breakpoints.down("sm")]: { padding: "12px" },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
    [theme.breakpoints.down("sm")]: { flexDirection: "column", alignItems: "stretch" },
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: "#1a1a2e",
    [theme.breakpoints.down("sm")]: { fontSize: 18 },
  },
  subtitle: {
    fontSize: 13,
    color: "#64748b",
    marginTop: 2,
  },
  controls: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: { flexDirection: "column", alignItems: "stretch" },
  },
  searchField: {
    backgroundColor: "#fff",
    borderRadius: 10,
    "& .MuiOutlinedInput-root": { borderRadius: 10, height: 40 },
    [theme.breakpoints.down("sm")]: { width: "100%" },
  },
  statusSelect: {
    backgroundColor: "#fff",
    borderRadius: 10,
    height: 40,
    minWidth: 150,
    "& .MuiOutlinedInput-root": { borderRadius: 10, height: 40 },
    [theme.breakpoints.down("sm")]: { minWidth: "unset", width: "100%" },
  },
  tableWrapper: {
    backgroundColor: "#fff",
    borderRadius: 14,
    boxShadow: "0 2px 12px rgba(0,0,0,.06)",
    overflow: "hidden",
    flex: 1,
    [theme.breakpoints.down("sm")]: { overflowX: "auto" },
  },
  tableHead: {
    backgroundColor: "var(--primary-color, #1a1a2e)",
    "& th": {
      color: "#fff",
      fontWeight: 700,
      fontSize: 12,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      padding: "12px 16px",
      borderBottom: "none",
    },
  },
  tableRow: {
    "&:hover": { backgroundColor: "#f8f7ff" },
    "& td": {
      fontSize: 14,
      color: "#374151",
      padding: "12px 16px",
      borderBottom: "1px solid #f3f4f6",
    },
  },
  actions: { display: "flex", gap: 4 },
  statusChip: { fontWeight: 600, fontSize: 11, height: 24 },
  emptyState: { textAlign: "center", padding: "60px 0", color: "#94a3b8" },
  paginationBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px",
    borderTop: "1px solid #f3f4f6",
    flexWrap: "wrap",
    gap: 8,
    [theme.breakpoints.down("sm")]: { padding: "8px 12px" },
  },
  actionBtn: {
    borderRadius: 6,
    textTransform: "none",
    fontWeight: 500,
  },
  coverPreview: {
    width: "100%",
    maxHeight: 160,
    objectFit: "cover",
    borderRadius: 8,
    marginTop: 8,
  },
  uploadArea: {
    border: "2px dashed #e2e8f0",
    borderRadius: 10,
    padding: "16px",
    textAlign: "center",
    cursor: "pointer",
    "&:hover": { borderColor: "var(--primary-color, #7c3aed)", backgroundColor: "#faf5ff" },
  },
  quillWrapper: {
    "& .ql-editor": { minHeight: 280, fontSize: 15 },
    "& .ql-container": { borderRadius: "0 0 8px 8px" },
    "& .ql-toolbar": { borderRadius: "8px 8px 0 0" },
  },
}));

const STATUS_MAP = {
  publicado: { label: "Publicado", color: "primary" },
  rascunho: { label: "Rascunho", color: "default" },
  inativo: { label: "Inativo", color: "secondary" },
};

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, 4, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link", "image", "video"],
    ["blockquote", "code-block"],
    ["clean"],
  ],
};

const EMPTY_FORM = { titulo: "", slug: "", resumo: "", conteudo: "", imagemCapa: "", status: "rascunho" };

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD":   return action.payload;
    case "ADD":    return [action.payload, ...state];
    case "UPDATE": return state.map((p) => (p.id === action.payload.id ? action.payload : p));
    case "DELETE": return state.filter((p) => p.id !== action.payload);
    default:       return state;
  }
};

const ROWS_PER_PAGE = 10;

const BlogPage = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const primaryColor = theme.palette.primary.main || "#7c3aed";
  const searchTimer = useRef(null);
  const fileRef = useRef(null);

  const [posts, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(true);
  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const totalPages = Math.max(1, Math.ceil(count / ROWS_PER_PAGE));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const data = await listBlogPosts({ searchParam, status: statusFilter, pageNumber });
      dispatch({ type: "LOAD", payload: data.posts });
      setCount(data.count);
      setHasMore(data.hasMore);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPageNumber(1); fetchPosts(); }, 400);
    return () => clearTimeout(searchTimer.current);
  }, [searchParam, statusFilter]);

  useEffect(() => { fetchPosts(); }, [pageNumber]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (post) => {
    setEditing(post);
    setForm({
      titulo: post.titulo,
      slug: post.slug,
      resumo: post.resumo || "",
      conteudo: post.conteudo || "",
      imagemCapa: post.imagemCapa || "",
      status: post.status,
    });
    setDialogOpen(true);
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const filename = await uploadBlogImagem(file);
      setForm((f) => ({ ...f, imagemCapa: filename }));
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.titulo.trim()) { toast.error("O título é obrigatório."); return; }
    setSaving(true);
    try {
      if (editing) {
        const updated = await updateBlogPost(editing.id, form);
        dispatch({ type: "UPDATE", payload: updated });
        toast.success("Post atualizado.");
      } else {
        const created = await createBlogPost(form);
        dispatch({ type: "ADD", payload: created });
        toast.success("Post criado.");
      }
      setDialogOpen(false);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteBlogPost(deletingId);
      dispatch({ type: "DELETE", payload: deletingId });
      toast.success("Post excluído.");
    } catch (err) {
      toastError(err);
    }
    setDeletingId(null);
  };

  const toggleStatus = async (post) => {
    const next = post.status === "publicado" ? "inativo" : "publicado";
    try {
      const updated = await updateBlogPost(post.id, { status: next });
      dispatch({ type: "UPDATE", payload: updated });
    } catch (err) {
      toastError(err);
    }
  };

  const coverUrl = (filename) =>
    filename ? `${getBackendUrl()}/public/${filename}` : null;

  return (
    <Box className={classes.root} style={{ "--primary-color": primaryColor }}>
      {/* Header */}
      <Box className={classes.header}>
        <Box>
          <Typography className={classes.title}>Blog</Typography>
          <Typography className={classes.subtitle}>{count} post(s) cadastrado(s)</Typography>
        </Box>

        <Box className={classes.controls}>
          <Select
            variant="outlined"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={classes.statusSelect}
            displayEmpty
          >
            <MenuItem value="">Todos os status</MenuItem>
            <MenuItem value="publicado">Publicado</MenuItem>
            <MenuItem value="rascunho">Rascunho</MenuItem>
            <MenuItem value="inativo">Inativo</MenuItem>
          </Select>

          <TextField
            placeholder="Pesquisar..."
            variant="outlined"
            size="small"
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "#94a3b8", fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={openCreate}
            style={{ borderRadius: 10, textTransform: "none", fontWeight: 600, height: 40 }}
          >
            Novo Post
          </Button>
        </Box>
      </Box>

      {/* Tabela */}
      <Box className={classes.tableWrapper}>
        <Table size="small">
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell>Título</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Data</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={6} />
            ) : posts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Box className={classes.emptyState}>
                    <ArticleIcon style={{ fontSize: 48, marginBottom: 8 }} />
                    <Typography>Nenhum post encontrado</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              posts.map((post) => {
                const st = STATUS_MAP[post.status] || STATUS_MAP.rascunho;
                return (
                  <TableRow key={post.id} className={classes.tableRow}>
                    <TableCell>
                      <Typography style={{ fontWeight: 600, fontSize: 14 }}>{post.titulo}</Typography>
                      {post.resumo && (
                        <Typography style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
                          {post.resumo.length > 80 ? post.resumo.slice(0, 80) + "…" : post.resumo}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell style={{ fontFamily: "monospace", fontSize: 12, color: "#64748b" }}>
                      {post.slug}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={st.label}
                        color={st.color}
                        size="small"
                        className={classes.statusChip}
                      />
                    </TableCell>
                    <TableCell style={{ whiteSpace: "nowrap", fontSize: 12 }}>
                      {new Date(post.createdAt).toLocaleDateString("pt-BR")}
                    </TableCell>
                    <TableCell align="center">
                      {isMobile ? (
                        <MobileActionMenu actions={[
                          { label: post.status === "publicado" ? "Despublicar" : "Publicar", icon: post.status === "publicado" ? <PublishIcon fontSize="small" /> : <UnpublishedIcon fontSize="small" />, onClick: () => toggleStatus(post) },
                          { label: "Editar", icon: <EditIcon fontSize="small" />, onClick: () => openEdit(post) },
                          { label: "Excluir", icon: <DeleteOutlineIcon fontSize="small" />, onClick: () => { setDeletingId(post.id); setConfirmOpen(true); }, color: "#ef4444" },
                        ]} />
                      ) : (
                        <Box className={classes.actions}>
                          <Tooltip title={post.status === "publicado" ? "Despublicar" : "Publicar"}>
                            <IconButton
                              size="small"
                              onClick={() => toggleStatus(post)}
                              style={{ color: post.status === "publicado" ? "#10b981" : "#94a3b8" }}
                            >
                              {post.status === "publicado"
                                ? <PublishIcon fontSize="small" />
                                : <UnpublishedIcon fontSize="small" />
                              }
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => openEdit(post)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton size="small" onClick={() => { setDeletingId(post.id); setConfirmOpen(true); }}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {count > 0 && (
          <Box className={classes.paginationBar}>
            <Typography variant="body2" style={{ color: "#666" }}>
              Exibindo {(pageNumber - 1) * ROWS_PER_PAGE + 1} a{" "}
              {Math.min(pageNumber * ROWS_PER_PAGE, count)} de {count} resultado(s)
            </Typography>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Button
                size="small"
                disabled={pageNumber === 1}
                onClick={() => setPageNumber((p) => p - 1)}
                className={classes.actionBtn}
              >
                Anterior
              </Button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pg = i + 1;
                if (totalPages > 5) {
                  const start = Math.max(1, Math.min(pageNumber - 2, totalPages - 4));
                  pg = start + i;
                }
                return (
                  <Button
                    key={pg}
                    size="small"
                    variant={pg === pageNumber ? "contained" : "text"}
                    color={pg === pageNumber ? "primary" : "default"}
                    onClick={() => setPageNumber(pg)}
                    style={{ minWidth: 32, borderRadius: 6, fontWeight: pg === pageNumber ? 700 : 400 }}
                  >
                    {pg}
                  </Button>
                );
              })}
              <Button
                size="small"
                disabled={!hasMore}
                onClick={() => setPageNumber((p) => p + 1)}
                className={classes.actionBtn}
              >
                Próximo
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* Dialog criar/editar */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editing ? "Editar Post" : "Novo Post"}</DialogTitle>
        <DialogContent>
          <Box style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 8 }}>
            <TextField
              label="Título *"
              variant="outlined"
              fullWidth
              value={form.titulo}
              onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, titulo: v })); }}
            />
            <TextField
              label="Slug (URL amigável)"
              variant="outlined"
              fullWidth
              value={form.slug}
              onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, slug: v })); }}
              helperText="Deixe em branco para gerar automaticamente a partir do título"
            />
            <TextField
              label="Resumo"
              variant="outlined"
              fullWidth
              multiline
              minRows={2}
              value={form.resumo}
              onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, resumo: v })); }}
            />

            {/* Conteúdo rico */}
            <Box>
              <Typography variant="caption" style={{ color: "#64748b", marginBottom: 4, display: "block" }}>
                Conteúdo *
              </Typography>
              <Box className={classes.quillWrapper}>
                <ReactQuill
                  theme="snow"
                  value={form.conteudo}
                  onChange={(val) => setForm((f) => ({ ...f, conteudo: val }))}
                  modules={QUILL_MODULES}
                />
              </Box>
            </Box>

            {/* Upload imagem de capa */}
            <Box>
              <Typography variant="caption" style={{ color: "#64748b", marginBottom: 4, display: "block" }}>
                Imagem de capa
              </Typography>
              <input
                type="file"
                accept="image/*"
                ref={fileRef}
                style={{ display: "none" }}
                onChange={handleCoverUpload}
              />
              <Box className={classes.uploadArea} onClick={() => !uploading && fileRef.current.click()}>
                {uploading ? (
                  <Box style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2">Enviando...</Typography>
                  </Box>
                ) : (
                  <Box style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: "#64748b" }}>
                    <CloudUploadIcon />
                    <Typography variant="body2">
                      {form.imagemCapa ? "Trocar imagem de capa" : "Clique para fazer upload da imagem de capa"}
                    </Typography>
                  </Box>
                )}
              </Box>
              {form.imagemCapa && coverUrl(form.imagemCapa) && (
                <img
                  src={coverUrl(form.imagemCapa)}
                  alt="Capa"
                  className={classes.coverPreview}
                  onError={(e) => {
                    const fallback = coverUrl(form.imagemCapa.replace(/\/blog\//, "/"));
                    if (e.target.src !== fallback) e.target.src = fallback;
                  }}
                />
              )}
            </Box>

            <Select
              variant="outlined"
              value={form.status}
              onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, status: v })); }}
              fullWidth
            >
              <MenuItem value="rascunho">Rascunho</MenuItem>
              <MenuItem value="publicado">Publicado</MenuItem>
              <MenuItem value="inativo">Inativo</MenuItem>
            </Select>
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving} variant="contained" color="primary">
            {saving ? "Salvando…" : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title="Excluir post"
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
      >
        Tem certeza que deseja excluir este post? Esta ação não pode ser desfeita.
      </ConfirmationModal>
    </Box>
  );
};

export default BlogPage;
